import {
    ButtonPrimary,
    ButtonSecondary,
    ConfirmDialog,
    ModalDialog,
    TextInput,
} from '@amelio/ui-library';
import { useState } from 'react';

import {
    getAvailableDashboardTabs,
    isCycleEvaluationDashboardTab,
} from './components/CycleEvaluationTabs/CycleEvaluationTabs';
import { Dashboard } from './components/Dashboard';
import { SentCyclesList } from './components/SentCyclesComponents/SentCylesList';
import { isDeactivatedGroupStatus } from './cycleStatus';
import { useDashboardActionMenus } from './useDashboardActionMenus';
import { useDashboardFilters } from './useDashboardFilters';

import type { OpenSentCyclesPayload } from './useDashboardActionMenus';
import type { TranslationKey } from '@/i18n-key';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationDashboardTab } from '@/types/cycle-evaluation/CycleEvaluationDashboardTab';
import type { FormEvent } from 'react';

import {
    useDeleteCycleEvaluation,
    useGetCycleEvaluationList,
    useUpdateCycleEvaluation,
} from '@/api/cycle-evaluation-services/cycle-evaluation';
import {
    getCycleEvaluationGroupDeletionConflict,
    isDuplicateCycleNameError,
    useDeleteCycleEvaluationGroup,
    useSendCycleEvaluationGroupReminders,
    useUpdateCycleEvaluationGroup,
    type CycleEvaluationGroupDeletionConflict,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import {
    useGetCycleEvaluationGroupDeletionImpact,
    useGetCycleEvaluationGroupList,
    useGetCycleEvaluationGroupReactivationImpact,
    useGetCycleEvaluationGroupReminderRecipients,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupQueries';
import { useUpdateCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import { FormField } from '@/components/FormField/FormField';
import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { Permission } from '@/constants/Permission';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { useOptionalRouter } from '@/hooks/utils/useOptionalRouter';
import { CYCLE_EVALUATION_GROUP_RENDERABLE_STEPS } from '@/navigation/CycleEvaluation/cycleEvaluationGroupRenderableSteps';
import {
    buildCycleEvaluationGroupUpdatePayload,
    getCycleEvaluationGroupLaunchSummaryPath,
    getCycleEvaluationGroupStepPath,
    getNewCycleEvaluationGroupPath,
    resolveCycleEvaluationGroupLandingPath,
} from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { RouteName } from '@/navigation/types';
import {
    CYCLE_NAME_MAX_LENGTH,
    NAME_REQUIRED,
    NAME_TOO_LONG,
} from '@/pages/CycleEvaluation/CycleInformation/validation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import {
    CycleEvaluationGroupStatus,
    type CycleEvaluationGroup,
} from '@/types/cycle-evaluation/CycleEvaluationGroup';
import { getLocalizedText } from '@/utils/localization';

const MIN_CYCLE_NAME_LENGTH = 3;

const DUPLICATE_GROUP_NAME_MESSAGE: TranslationKey =
    'cycleEvaluationCycleInformation.validation.nameAlreadyUsed';

/** A caller can land straight on one tab -- `history.push(path, { tab: 'objectivePeriods' })`. */
interface DashboardLocationState {
    tab?: unknown;
}

/**
 * The tab the caller asked to land on, if any. Anything it did not spell as a known tab (a stale
 * link, a renamed tab) means "no tab in particular", so the dashboard opens on its own default
 * instead of an empty panel. The page also renders outside a Router (Storybook, the MFE host before
 * its router mounts), and landing on a tab is a nicety rather than a requirement -- so that case
 * reads as "none asked for" too, through the same door `useNavigate` uses for a missing history.
 */
const useRequestedDashboardTab = (): CycleEvaluationDashboardTab | undefined => {
    const state = useOptionalRouter()?.location.state as DashboardLocationState | undefined;

    return isCycleEvaluationDashboardTab(state?.tab) ? state.tab : undefined;
};

export const DashboardPage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const requestedTab = useRequestedDashboardTab();
    // Two audiences share this page: the cycle administrators, and the objectives managers who
    // come for the objective periods. The cycle queries only run for the former -- the latter
    // may lack the performance scope those endpoints demand.
    const { can, isLoading: arePermissionsLoading } = useUserPermissions();
    const canManageEvaluationCycles = can(Permission.ManageAllDepartmentsPerformance);
    const availableTabs = getAvailableDashboardTabs({
        canManageEvaluationCycles,
        canManageObjectivePeriods: can(Permission.ManageAllEmployeesObjectives),
    });
    const selectedLanguage = useSelectedAppLanguage();
    const { handleError } = useError();

    const [currentEvaluation, setCurrentEvaluation] = useState<CycleEvaluation | undefined>(
        undefined,
    );
    const [evaluationToDelete, setEvaluationToDelete] = useState<CycleEvaluation | undefined>(
        undefined,
    );
    const [groupToDelete, setGroupToDelete] = useState<CycleEvaluationGroup | undefined>(undefined);
    const [evaluationToRename, setEvaluationToRename] = useState<CycleEvaluation | undefined>(
        undefined,
    );
    const [groupToRename, setGroupToRename] = useState<CycleEvaluationGroup | undefined>(undefined);
    const [groupToRemind, setGroupToRemind] = useState<CycleEvaluationGroup | undefined>(undefined);
    const [groupDeletionConflict, setGroupDeletionConflict] = useState<
        CycleEvaluationGroupDeletionConflict | undefined
    >(undefined);
    const [newName, setNewName] = useState<string | undefined>(undefined);
    const [renameErrorKey, setRenameErrorKey] = useState<TranslationKey | undefined>(undefined);
    const [isSentCyclesOpen, setIsSentCyclesOpen] = useState(false);
    // Kept after the sent cycles close so their content stays in place while the dialog fades out.
    const [sentCyclesTarget, setSentCyclesTarget] = useState<OpenSentCyclesPayload | undefined>(
        undefined,
    );
    const [groupPendingStatusChange, setGroupPendingStatusChange] = useState<
        CycleEvaluationGroup | undefined
    >(undefined);

    const {
        data: cycleEvaluationList,
        isError,
        isPending,
        refetch,
        isRefetching,
    } = useGetCycleEvaluationList({ enabled: canManageEvaluationCycles });

    const {
        data: cycleEvaluationGroupList,
        isError: isGroupListError,
        isPending: isGroupListPending,
        isRefetching: isGroupListRefetching,
    } = useGetCycleEvaluationGroupList({ enabled: canManageEvaluationCycles });

    const { data: groupDeletionImpact } = useGetCycleEvaluationGroupDeletionImpact(
        groupToDelete && !groupDeletionConflict ? groupToDelete.id : undefined,
    );

    const { data: groupReminderRecipients, isError: isReminderRecipientsError } =
        useGetCycleEvaluationGroupReminderRecipients(groupToRemind?.id);

    const { data: reactivationImpact } = useGetCycleEvaluationGroupReactivationImpact(
        groupPendingStatusChange && isDeactivatedGroupStatus(groupPendingStatusChange.status)
            ? groupPendingStatusChange.id
            : undefined,
    );

    const { mutateAsync: deleteCycleEvaluation, isPending: isDeletingEvaluation } =
        useDeleteCycleEvaluation();
    const { mutateAsync: updateCycleEvaluation, isPending: isUpdating } =
        useUpdateCycleEvaluation();
    const { mutateAsync: updateCycleEvaluationGroup, isPending: isUpdatingGroup } =
        useUpdateCycleEvaluationGroup();
    const { mutateAsync: deleteCycleEvaluationGroup, isPending: isDeletingGroup } =
        useDeleteCycleEvaluationGroup();
    const { mutateAsync: sendCycleEvaluationGroupReminders, isPending: isSendingReminders } =
        useSendCycleEvaluationGroupReminders();
    const { mutateAsync: updateCycleQuestionnaire, isPending: isRenaming } =
        useUpdateCycleQuestionnaire();

    const {
        searchText,
        setSearchText,
        statusFilter,
        statusFilterOptions,
        onToggleStatusFilter,
        typeFilter,
        typeFilterOptions,
        onToggleTypeFilter,
        cycleEvaluationGroups,
        cycleEvaluations,
        currentPage,
        onCyclePageChange,
        totalCycleCount,
    } = useDashboardFilters({ cycleEvaluationList, cycleEvaluationGroupList });

    const onCreateCycle = () => {
        navigate({ path: getNewCycleEvaluationGroupPath(RouteName.CycleEvaluationGroupForm) });
    };

    const onEditEvaluation = (cycleEvaluation: CycleEvaluation) => {
        navigate({
            path: `${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(CycleEvaluationGroupSteps.CYCLE_INFORMATION)}/${cycleEvaluation.id}`,
        });
    };

    const onEditGroup = (group: CycleEvaluationGroup) => {
        navigate({
            path: resolveCycleEvaluationGroupLandingPath({
                group,
                renderableSteps: CYCLE_EVALUATION_GROUP_RENDERABLE_STEPS,
            }),
        });
    };

    // A draft has no second rule of its own: it goes to the launch summary, where the guard is
    // evaluated, names every reason a launch is refused, and owns the button that performs it.
    const onLaunchGroup = (group: CycleEvaluationGroup) => {
        navigate({ path: getCycleEvaluationGroupLaunchSummaryPath(group.id) });
    };

    const onToggleGroupStatus = (group: CycleEvaluationGroup) => {
        setGroupPendingStatusChange(group);
    };

    const onCancelToggleGroupStatus = () => {
        setGroupPendingStatusChange(undefined);
    };

    const onConfirmToggleGroupStatus = async () => {
        if (!groupPendingStatusChange) {
            return;
        }

        const nextStatus =
            groupPendingStatusChange.status === CycleEvaluationGroupStatus.ACTIVE
                ? CycleEvaluationGroupStatus.DEACTIVATED
                : CycleEvaluationGroupStatus.ACTIVE;

        try {
            await updateCycleEvaluationGroup({
                id: groupPendingStatusChange.id,
                payload: buildCycleEvaluationGroupUpdatePayload({
                    group: groupPendingStatusChange,
                    status: nextStatus,
                }),
            });

            showSuccessToast({
                message: t(
                    nextStatus === CycleEvaluationGroupStatus.ACTIVE
                        ? 'cycleEvaluationDashboard.message.activateSuccess'
                        : 'cycleEvaluationDashboard.message.deactivateSuccess',
                ),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setGroupPendingStatusChange(undefined);
        }
    };

    const onActivateEvaluation = async (cycleEvaluation: CycleEvaluation) => {
        setCurrentEvaluation(cycleEvaluation);

        try {
            await updateCycleEvaluation({
                ...cycleEvaluation,
                status:
                    cycleEvaluation.status === CycleEvaluationStatus.ACTIVE
                        ? CycleEvaluationStatus.INACTIVE
                        : CycleEvaluationStatus.ACTIVE,
            });

            showSuccessToast({
                message: t(
                    cycleEvaluation.status === CycleEvaluationStatus.ACTIVE
                        ? 'cycleEvaluationDashboard.message.deactivateSuccess'
                        : 'cycleEvaluationDashboard.message.activateSuccess',
                ),
            });

            refetch();
        } catch (error) {
            handleError({ error });
        } finally {
            setCurrentEvaluation(undefined);
        }
    };

    const onDeleteEvaluation = (cycleEvaluation: CycleEvaluation) => {
        setEvaluationToDelete(cycleEvaluation);
    };

    const onConfirmDelete = async () => {
        if (!evaluationToDelete) {
            return;
        }

        try {
            setCurrentEvaluation(evaluationToDelete);
            await deleteCycleEvaluation(evaluationToDelete);

            showSuccessToast({
                message: t('cycleEvaluationDashboard.message.deleteSuccess'),
            });

            await refetch();
        } catch (error) {
            handleError({ error });
        } finally {
            setCurrentEvaluation(undefined);
            setEvaluationToDelete(undefined);
        }
    };

    const onDeleteGroup = (group: CycleEvaluationGroup) => {
        setGroupToDelete(group);
    };

    const onConfirmDeleteGroup = async () => {
        if (!groupToDelete) {
            return;
        }

        try {
            const deleted = await deleteCycleEvaluationGroup(groupToDelete.id);

            showSuccessToast({
                message: deleted.deletedGeneratedEvaluationsCount
                    ? t('cycleEvaluationDashboard.message.deleteGroupSuccessWithEvaluations', {
                          generatedCount: deleted.deletedGeneratedEvaluationsCount,
                      })
                    : t('cycleEvaluationDashboard.message.deleteSuccess'),
            });

            setGroupToDelete(undefined);
        } catch (error) {
            const conflict = getCycleEvaluationGroupDeletionConflict(error);

            if (!conflict) {
                handleError({ error });
                setGroupToDelete(undefined);

                return;
            }

            if (groupToDelete.status === CycleEvaluationGroupStatus.COMPLETED) {
                showWarningToast({
                    title: t('cycleEvaluationDashboard.deleteGroup.blockedTitle'),
                    message: t('cycleEvaluationDashboard.deleteGroup.blockedMessageCompleted', {
                        submittedCount: conflict.submittedEvaluationsCount,
                        generatedCount: conflict.generatedEvaluationsCount,
                    }),
                });
                setGroupToDelete(undefined);

                return;
            }

            if (isDeactivatedGroupStatus(groupToDelete.status)) {
                showWarningToast({
                    title: t('cycleEvaluationDashboard.deleteGroup.blockedTitle'),
                    message: t('cycleEvaluationDashboard.deleteGroup.blockedMessageDeactivated'),
                });
                setGroupToDelete(undefined);

                return;
            }

            setGroupDeletionConflict(conflict);
        }
    };

    const onDeactivateInsteadOfDelete = async () => {
        if (!groupToDelete) {
            return;
        }

        try {
            await updateCycleEvaluationGroup({
                id: groupToDelete.id,
                payload: buildCycleEvaluationGroupUpdatePayload({
                    group: groupToDelete,
                    status: CycleEvaluationGroupStatus.DEACTIVATED,
                }),
            });

            showSuccessToast({
                message: t('cycleEvaluationDashboard.message.deactivateSuccess'),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setGroupToDelete(undefined);
            setGroupDeletionConflict(undefined);
        }
    };

    const onSendReminders = (group: CycleEvaluationGroup) => {
        setGroupToRemind(group);
    };

    const onCancelSendReminders = () => {
        setGroupToRemind(undefined);
    };

    const onConfirmSendReminders = async () => {
        if (!groupToRemind) {
            return;
        }

        try {
            const { remindedCount } = await sendCycleEvaluationGroupReminders(groupToRemind.id);

            showSuccessToast({
                message: t('cycleEvaluationDashboard.sendReminders.successMessage', {
                    count: remindedCount,
                }),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setGroupToRemind(undefined);
        }
    };

    const onCancelDeleteDialog = () => {
        setEvaluationToDelete(undefined);
        setGroupToDelete(undefined);
        setGroupDeletionConflict(undefined);
    };

    const onCloseRenameModal = () => {
        setEvaluationToRename(undefined);
        setGroupToRename(undefined);
        setNewName(undefined);
        setRenameErrorKey(undefined);
    };

    // The action menu hands over whatever row it holds, which can be nothing at all.
    const onRenameEvaluation = async (cycleEvaluation?: CycleEvaluation) => {
        if (!cycleEvaluation) {
            return;
        }

        const trimmedName = newName?.trim() || '';

        if (trimmedName.length < MIN_CYCLE_NAME_LENGTH) {
            setRenameErrorKey('evaluation360Questionnaire.validation.questionnaireTitleMin');

            return;
        }

        try {
            // `cycleQuestionnaire` is typed as required, but the service casts the server response
            // with no runtime validation, so an incomplete legacy record must not crash here.
            await updateCycleQuestionnaire({
                ...cycleEvaluation.cycleQuestionnaire,
                title: {
                    ...cycleEvaluation.cycleQuestionnaire?.title,
                    [selectedLanguage]: trimmedName,
                },
            });

            showSuccessToast({
                message: t('cycleEvaluationDashboard.message.renameSuccess'),
            });

            // Marks the renamed row busy while the list refetches its new title.
            setCurrentEvaluation(cycleEvaluation);
            refetch();
        } catch (error) {
            handleError({ error });
        } finally {
            onCloseRenameModal();
        }
    };

    const onOpenRenameModal = (cycleEvaluation: CycleEvaluation) => {
        setEvaluationToRename(cycleEvaluation);
        // The group rename wins on submit, so a group still held from an earlier rename would
        // otherwise be renamed in place of this evaluation.
        setGroupToRename(undefined);
        setRenameErrorKey(undefined);
        // Same guard as the search filter below: a legacy record missing its questionnaire falls
        // back to the evaluation's own title instead of throwing on `.title` of `undefined`.
        setNewName(
            getLocalizedText(cycleEvaluation.cycleQuestionnaire?.title) ||
                cycleEvaluation.title ||
                '',
        );
    };

    const onOpenRenameGroupModal = (group: CycleEvaluationGroup) => {
        setEvaluationToRename(undefined);
        setGroupToRename(group);
        setNewName(group.name);
        setRenameErrorKey(undefined);
    };

    const onRenameFieldChange = (value: string) => {
        setNewName(value);
        setRenameErrorKey(undefined);
    };

    const onRenameGroup = async (group: CycleEvaluationGroup) => {
        const trimmedName = newName?.trim() || '';

        if (!trimmedName) {
            setRenameErrorKey(NAME_REQUIRED);

            return;
        }

        if (trimmedName.length > CYCLE_NAME_MAX_LENGTH) {
            setRenameErrorKey(NAME_TOO_LONG);

            return;
        }

        try {
            await updateCycleEvaluationGroup({
                id: group.id,
                payload: buildCycleEvaluationGroupUpdatePayload({
                    group,
                    name: trimmedName,
                }),
            });

            showSuccessToast({
                message: t('cycleEvaluationDashboard.message.renameSuccess'),
            });
        } catch (error) {
            if (isDuplicateCycleNameError(error)) {
                showWarningToast({ message: t(DUPLICATE_GROUP_NAME_MESSAGE) });
            } else {
                handleError({ error });
            }
        } finally {
            onCloseRenameModal();
        }
    };

    const onSubmitRename = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Enter pressed again while the first rename is still sending must not send it twice.
        if (isRenaming || isUpdatingGroup) {
            return;
        }

        if (groupToRename) {
            void onRenameGroup(groupToRename);
        } else if (evaluationToRename) {
            void onRenameEvaluation(evaluationToRename);
        }
    };

    const onOpenSentCycles = (payload: OpenSentCyclesPayload) => {
        setSentCyclesTarget(payload);
        setIsSentCyclesOpen(true);
    };

    const onCloseSentCycles = () => {
        setIsSentCyclesOpen(false);
    };

    const { actionMenuItems, groupActionMenuItems } = useDashboardActionMenus({
        cycleEvaluationList,
        isRefetching,
        isGroupListRefetching,
        isUpdatingGroup,
        isDeletingGroup,
        hasPendingStatusConfirmation: Boolean(groupPendingStatusChange),
        onEditEvaluation,
        onActivateEvaluation,
        onOpenRenameModal,
        onDeleteEvaluation,
        onEditGroup,
        onToggleGroupStatus,
        onLaunchGroup,
        onOpenRenameGroupModal,
        onDeleteGroup,
        onSendReminders,
        onOpenSentCycles,
    });

    useFetchErrorsHandler({ errors: [isError, isGroupListError, isReminderRecipientsError] });

    const deleteDialogContent = groupToDelete
        ? groupDeletionConflict
            ? {
                  title: t('cycleEvaluationDashboard.deleteGroup.blockedTitle'),
                  message: t('cycleEvaluationDashboard.deleteGroup.blockedMessage', {
                      submittedCount: groupDeletionConflict.submittedEvaluationsCount,
                      generatedCount: groupDeletionConflict.generatedEvaluationsCount,
                  }),
                  confirmLabel: t('common.deactivate'),
                  onConfirm: onDeactivateInsteadOfDelete,
                  isDestructive: false,
              }
            : {
                  title: t('cycleEvaluationDashboard.deleteGroup.confirmTitle'),
                  message: groupDeletionImpact
                      ? t('cycleEvaluationDashboard.deleteGroup.confirmMessageWithCounts', {
                            evaluations: t(
                                'cycleEvaluationDashboard.deleteGroup.generatedEvaluations',
                                {
                                    count: groupDeletionImpact.generatedEvaluationsCount,
                                },
                            ),
                            emails: t('cycleEvaluationDashboard.deleteGroup.scheduledEmails', {
                                count: groupDeletionImpact.scheduledEmailsCount,
                            }),
                        })
                      : t('cycleEvaluationDashboard.deleteGroup.confirmMessage'),
                  confirmLabel: t('common.delete'),
                  onConfirm: onConfirmDeleteGroup,
                  isDestructive: true,
              }
        : {
              title: t('common.deleteConfirmTitle'),
              message: t('common.deleteConfirmMessage'),
              confirmLabel: t('common.delete'),
              onConfirm: onConfirmDelete,
              isDestructive: true,
          };

    const getSendRemindersDialogContent = () => {
        if (!groupReminderRecipients || groupReminderRecipients.nonRespondentCount === undefined) {
            return undefined;
        }

        const { nonRespondentCount, hasGeneratedEvaluations } = groupReminderRecipients;

        if (nonRespondentCount === 0) {
            return {
                message: t(
                    hasGeneratedEvaluations
                        ? 'cycleEvaluationDashboard.sendReminders.emptyMessage'
                        : 'cycleEvaluationDashboard.sendReminders.nothingSentMessage',
                ),
                confirmLabel: t('common.close'),
                onConfirm: onCancelSendReminders,
                hideCancel: true,
            };
        }

        return {
            message: t('cycleEvaluationDashboard.sendReminders.confirmMessage', {
                count: nonRespondentCount,
            }),
            confirmLabel: t('common.confirm'),
            onConfirm: onConfirmSendReminders,
            hideCancel: false,
        };
    };

    const sendRemindersDialogContent = getSendRemindersDialogContent();

    const getToggleGroupStatusDialogContent = () => {
        if (!groupPendingStatusChange) {
            return undefined;
        }

        if (!isDeactivatedGroupStatus(groupPendingStatusChange.status)) {
            return {
                title: t('cycleEvaluationDashboard.deactivateGroup.confirmTitle'),
                message: t('cycleEvaluationDashboard.deactivateGroup.confirmMessage'),
                confirmLabel: t('common.deactivate'),
            };
        }

        if (!reactivationImpact) {
            return undefined;
        }

        const nothingSkipped =
            reactivationImpact.skippedMilestonesCount === 0 &&
            reactivationImpact.skippedRemindersCount === 0;

        return {
            title: t('cycleEvaluationDashboard.reactivateGroup.confirmTitle'),
            message: nothingSkipped
                ? t('cycleEvaluationDashboard.reactivateGroup.nothingSkippedMessage')
                : t('cycleEvaluationDashboard.reactivateGroup.confirmMessage', {
                      milestones: t('cycleEvaluationDashboard.reactivateGroup.skippedMilestones', {
                          count: reactivationImpact.skippedMilestonesCount,
                      }),
                      reminders: t('cycleEvaluationDashboard.reactivateGroup.skippedReminders', {
                          count: reactivationImpact.skippedRemindersCount,
                      }),
                  }),
            confirmLabel: t('cycleEvaluationDashboard.menuItem.reactivate'),
        };
    };

    const toggleGroupStatusDialogContent = getToggleGroupStatusDialogContent();

    return (
        <>
            <Dashboard
                cycleEvaluationGroups={cycleEvaluationGroups}
                cycleEvaluations={cycleEvaluations}
                allCycleEvaluations={cycleEvaluationList}
                actionMenuItems={actionMenuItems}
                groupActionMenuItems={groupActionMenuItems}
                // A permission not yet answered reads as "no", so until it lands the dashboard
                // knows neither which tabs this reader may open nor which queries to send. It
                // waits rather than showing a tab bar it would have to correct.
                isLoading={
                    arePermissionsLoading ||
                    (canManageEvaluationCycles && (isPending || isGroupListPending))
                }
                isUpdating={isUpdating}
                isRefetching={isRefetching}
                currentEvaluation={currentEvaluation}
                searchText={searchText}
                setSearchText={setSearchText}
                statusFilter={statusFilter}
                statusFilterOptions={statusFilterOptions}
                onStatusFilterChange={onToggleStatusFilter}
                typeFilter={typeFilter}
                typeFilterOptions={typeFilterOptions}
                onTypeFilterChange={onToggleTypeFilter}
                onCreateCycle={onCreateCycle}
                onEditEvaluation={onEditEvaluation}
                onEditGroup={onEditGroup}
                initialTab={requestedTab}
                availableTabs={availableTabs}
                currentPage={currentPage}
                totalCycleCount={totalCycleCount}
                onCyclePageChange={onCyclePageChange}
            />

            <ModalDialog
                isOpen={Boolean(evaluationToRename || groupToRename)}
                onClose={onCloseRenameModal}
                title={t('cycleEvaluationDashboard.rename.title')}
                size={'md'}
                closeAriaLabel={t('common.close')}
                backdropBlur
                dataTestId={'rename-modal'}
                onSubmit={onSubmitRename}
                footer={
                    <div className={'flex justify-end gap-2'}>
                        <ButtonSecondary
                            onClick={onCloseRenameModal}
                            size={'md'}
                            dataTestId={'close-rename'}
                        >
                            {t('common.cancel')}
                        </ButtonSecondary>

                        <ButtonPrimary
                            type={'submit'}
                            size={'md'}
                            loading={isRenaming || isUpdatingGroup}
                            dataTestId={'confirm-rename'}
                        >
                            {t('common.confirm')}
                        </ButtonPrimary>
                    </div>
                }
            >
                <FormField
                    label={t('cycleEvaluationCycleInformation.name.label')}
                    id={'rename-cycle'}
                    dataTestId={'rename-cycle-field'}
                    errorMessage={renameErrorKey ? t(renameErrorKey) : undefined}
                >
                    <TextInput
                        id={'rename-cycle'}
                        value={newName || ''}
                        error={Boolean(renameErrorKey)}
                        onChange={onRenameFieldChange}
                        dataTestId={'rename-cycle'}
                    />
                </FormField>
            </ModalDialog>

            <SentCyclesList
                isOpen={isSentCyclesOpen}
                cycleEvaluationId={sentCyclesTarget?.cycleEvaluation.id ?? ''}
                cycleName={sentCyclesTarget?.cycleName ?? ''}
                evaluationTemplates={sentCyclesTarget?.evaluationTemplates ?? []}
                reviewType={
                    sentCyclesTarget?.cycleEvaluation.reviewType ?? EvaluationType.EVALUATION_360
                }
                onClose={onCloseSentCycles}
            />

            <ConfirmDialog
                isOpen={Boolean(evaluationToDelete) || Boolean(groupToDelete)}
                title={deleteDialogContent.title}
                message={deleteDialogContent.message}
                confirmLabel={deleteDialogContent.confirmLabel}
                cancelLabel={t('common.cancel')}
                onConfirm={deleteDialogContent.onConfirm}
                onCancel={onCancelDeleteDialog}
                isLoading={isDeletingEvaluation || isDeletingGroup || isUpdatingGroup}
                isDestructive={deleteDialogContent.isDestructive}
                dataTestId={'delete-cycle-dialog'}
            />

            {sendRemindersDialogContent ? (
                <ConfirmDialog
                    isOpen={Boolean(groupToRemind)}
                    title={t('cycleEvaluationDashboard.menuItem.sendReminders')}
                    message={sendRemindersDialogContent.message}
                    confirmLabel={sendRemindersDialogContent.confirmLabel}
                    cancelLabel={t('common.cancel')}
                    onConfirm={sendRemindersDialogContent.onConfirm}
                    onCancel={onCancelSendReminders}
                    isLoading={isSendingReminders}
                    hideCancel={sendRemindersDialogContent.hideCancel}
                    dataTestId={'send-reminders-dialog'}
                />
            ) : null}

            {toggleGroupStatusDialogContent ? (
                <ConfirmDialog
                    isOpen={Boolean(groupPendingStatusChange)}
                    title={toggleGroupStatusDialogContent.title}
                    message={toggleGroupStatusDialogContent.message}
                    confirmLabel={toggleGroupStatusDialogContent.confirmLabel}
                    cancelLabel={t('common.cancel')}
                    onConfirm={onConfirmToggleGroupStatus}
                    onCancel={onCancelToggleGroupStatus}
                    isLoading={isUpdatingGroup}
                    dataTestId={'toggle-group-status-dialog'}
                />
            ) : null}
        </>
    );
};
